import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import BugReport from "@mui/icons-material/BugReport";
import Lightbulb from "@mui/icons-material/Lightbulb";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Chat from "@mui/icons-material/Chat";
import CheckCircle from "@mui/icons-material/CheckCircle";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Send from "@mui/icons-material/Send";
import { useMutation } from "@apollo/client/react";
import { useTextDomainContext, useUser } from "@/app/providers";
import { mutations } from "@/graphql";
import { getClientEnvironmentInfo } from "./utils";
import { useAppSelector } from "@/app/store";

export type FeedbackType = "bug" | "feature" | "improvement" | "general";

interface FeedbackModalProps {
  open?: boolean;
  onClose?: () => void;
  initialType?: FeedbackType;
}

interface CategoryOption {
  type: FeedbackType;
  label: string;
  description: string;
  icon: ReactElement;
  color: string;
  bgLight: string;
  borderColor: string;
}

export const FeedbackModal = ({
  open,
  onClose,
  initialType = "bug",
}: FeedbackModalProps): ReactElement | null => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const tenantsList = useAppSelector((state) => state.auth.tenantsList);

  const [feedbackType, setFeedbackType] = useState<FeedbackType>(initialType);
  const [subject, setSubject] = useState("");
  const [comment, setComment] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [internalOpen, setInternalOpen] = useState(false);
  const isModalOpen = Boolean(open || internalOpen);

  const handleModalClose = () => {
    setInternalOpen(false);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const [submitFeedbackMutation, { loading }] = useMutation<any>(
    mutations.submitFeedback,
  );

  // Categories config
  const categories: CategoryOption[] = useMemo(
    () => [
      {
        type: "bug",
        label: gettext("Report a bug"),
        description: gettext("Something isn't working right"),
        icon: <BugReport sx={{ fontSize: 20 }} />,
        color: "#dc2626",
        bgLight: "#fef2f2",
        borderColor: "#fca5a5",
      },
      {
        type: "feature",
        label: gettext("Request a feature"),
        description: gettext("Idea for a new capability"),
        icon: <Lightbulb sx={{ fontSize: 20 }} />,
        color: "#7c3aed",
        bgLight: "#f5f3ff",
        borderColor: "#c4b5fd",
      },
      {
        type: "improvement",
        label: gettext("Improvement suggestion"),
        description: gettext("Ways to make things better"),
        icon: <TrendingUp sx={{ fontSize: 20 }} />,
        color: "#059669",
        bgLight: "#ecfdf5",
        borderColor: "#6ee7b7",
      },
      {
        type: "general",
        label: gettext("General"),
        description: gettext("Questions or comments"),
        icon: <Chat sx={{ fontSize: 20 }} />,
        color: "#0284c7",
        bgLight: "#f0f9ff",
        borderColor: "#7dd3fc",
      },
    ],
    [gettext],
  );

  const clientInfo = useMemo(() => getClientEnvironmentInfo(), [isModalOpen]);

  // Current corporate group name lookup
  const currentTenantName = useMemo(() => {
    if (user?.currentTenantId === "all") return gettext("All Corporate Groups");
    if (!user?.currentTenantId) return "";
    const matched = tenantsList?.find(
      (t: any) =>
        t.Id === user.currentTenantId ||
        t._id === user.currentTenantId ||
        t.id === user.currentTenantId,
    );
    return matched?.name || "";
  }, [user?.currentTenantId, tenantsList, gettext]);

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  // Reset or initialize state on dialog open
  useEffect(() => {
    if (isModalOpen) {
      setFeedbackType(initialType);
      setSubject("");
      setComment("");
      setIsSuccess(false);
      setErrorMessage(null);
      setShowDetails(false);
    }
  }, [isModalOpen, initialType]);

  // Handle custom open event from anywhere in the app
  useEffect(() => {
    const handleCustomOpen = (event: Event) => {
      const customEvent = event as CustomEvent<{ type?: FeedbackType }>;
      if (customEvent.detail?.type) {
        setFeedbackType(customEvent.detail.type);
      }
      setInternalOpen(true);
    };
    window.addEventListener("open-feedback-modal", handleCustomOpen);
    return () => {
      window.removeEventListener("open-feedback-modal", handleCustomOpen);
    };
  }, []);

  if (!user) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) {
      setErrorMessage(gettext("Please enter your feedback description."));
      return;
    }

    setErrorMessage(null);

    try {
      const variables = {
        feedbackType,
        subject: subject.trim(),
        comment: comment.trim(),
        pageUrl: currentPageUrl,
        corporateGroupId: user.currentTenantId || "",
        corporateGroupName: currentTenantName,
        parentCompanyId: user.currentTeamId ? String(user.currentTeamId) : "",
        parentCompanyName: user.currentTeam || "",
        childCompanyId: user.currentSubsidiaryId || "",
        childCompanyName: user.currentSubsidiary || "",
        browser: clientInfo.browser,
        os: clientInfo.os,
        device: clientInfo.device,
        screenResolution: clientInfo.screenResolution,
        extraData: JSON.stringify({
          userAgent: clientInfo.userAgent,
          timezone: clientInfo.timezone,
          language: user.lang || clientInfo.language,
          countryCode: user.countryCode || "",
          userRole: user.currentRoleKey || "",
          timestamp: new Date().toISOString(),
        }),
      };

      const res = await submitFeedbackMutation({ variables });
      const data = res.data?.submitFeedback;

      if (data?.success) {
        setIsSuccess(true);
      } else {
        const msg =
          data?.message?.[0] ||
          gettext("Failed to send feedback. Please try again.");
        setErrorMessage(msg);
      }
    } catch (err: any) {
      setErrorMessage(
        err.message || gettext("An error occurred while submitting feedback."),
      );
    }
  };

  const selectedCategory =
    categories.find((c) => c.type === feedbackType) || categories[0];

  return (
    <Dialog
      open={isModalOpen}
      onClose={loading ? undefined : handleModalClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1.5,
          pt: 2.5,
          px: 3,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: selectedCategory.bgLight,
              color: selectedCategory.color,
              transition: "all 0.2s ease",
            }}
          >
            {selectedCategory.icon}
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2 }}
            >
              {gettext("Send Feedback")}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.8rem" }}
            >
              {gettext("We value your input to help us improve Fabeke")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleModalClose}
          size="small"
          disabled={loading}
          sx={{
            color: "grey.500",
            "&:hover": { backgroundColor: "grey.100" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {isSuccess ? (
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#ecfdf5",
              color: "#059669",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px auto",
            }}
          >
            <CheckCircle sx={{ fontSize: 40 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            {gettext("Thank You!")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3,
              maxWidth: 420,
              mx: "auto",
            }}
          >
            {gettext(
              "Your feedback has been delivered to support@fabeke.com along with system details to assist our team. We appreciate your contribution!",
            )}
          </Typography>
          <Button
            variant="contained"
            onClick={handleModalClose}
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {gettext("Done")}
          </Button>
        </DialogContent>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            {errorMessage && (
              <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: 2 }}
                onClose={() => setErrorMessage(null)}
              >
                {errorMessage}
              </Alert>
            )}

            {/* Category selection */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
            >
              {gettext("Feedback Type")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.5,
                mb: 2.5,
              }}
            >
              {categories.map((cat) => {
                const isSelected = feedbackType === cat.type;
                return (
                  <Box
                    key={cat.type}
                    onClick={() => setFeedbackType(cat.type)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: "1.5px solid",
                      borderColor: isSelected ? cat.borderColor : "#e2e8f0",
                      backgroundColor: isSelected ? cat.bgLight : "#ffffff",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      "&:hover": {
                        borderColor: cat.borderColor,
                        backgroundColor: cat.bgLight,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: isSelected ? cat.color : "text.secondary",
                        mt: 0.2,
                      }}
                    >
                      {cat.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? cat.color : "text.primary",
                          fontSize: "0.85rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {cat.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.72rem",
                          display: "block",
                          mt: 0.3,
                        }}
                      >
                        {cat.description}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Subject */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 0.8, color: "text.primary" }}
            >
              {gettext("Subject")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={gettext("Brief summary of your feedback...")}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            {/* Description */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 0.8, color: "text.primary" }}
            >
              {gettext("Description")}{" "}
              <Box component="span" sx={{ color: "#ef4444" }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              required
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={gettext(
                "Please describe the issue or suggestion in detail. What were you doing? What did you expect to happen?",
              )}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            {/* Diagnostic Context Collapsible */}
            <Box
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                backgroundColor: "#f8fafc",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setShowDetails(!showDetails)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.25,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f1f5f9" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <InfoOutlined
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    {gettext(
                      "Diagnostics & System Info included with this report",
                    )}
                  </Typography>
                </Box>
                <ExpandMore
                  sx={{
                    fontSize: 18,
                    color: "text.secondary",
                    transform: showDetails ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Box>

              <Collapse in={showDetails}>
                <Divider />
                <Box
                  sx={{
                    p: 1.5,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    display: "grid",
                    gridTemplateColumns: "130px 1fr",
                    rowGap: 1,
                    columnGap: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {gettext("User")}:
                  </Typography>
                  <Typography variant="caption">
                    {user.name} ({user.email})
                  </Typography>

                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {gettext("Page URL")}:
                  </Typography>
                  <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                    {currentPageUrl}
                  </Typography>

                  {currentTenantName && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {gettext("Corporate Group")}:
                      </Typography>
                      <Typography variant="caption">
                        {currentTenantName}
                      </Typography>
                    </>
                  )}

                  {user.currentTeam && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {gettext("Parent Company")}:
                      </Typography>
                      <Typography variant="caption">
                        {user.currentTeam}
                      </Typography>
                    </>
                  )}

                  {user.currentSubsidiary && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {gettext("Child Company")}:
                      </Typography>
                      <Typography variant="caption">
                        {user.currentSubsidiary}
                      </Typography>
                    </>
                  )}

                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {gettext("Browser & OS")}:
                  </Typography>
                  <Typography variant="caption">
                    {clientInfo.browser} on {clientInfo.os}
                  </Typography>

                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {gettext("IP & Location")}:
                  </Typography>
                  <Typography variant="caption">
                    {gettext("Captured securely by server (Country, City)")}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid #f1f5f9",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={handleModalClose}
              disabled={loading}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              {gettext("Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !comment.trim()}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Send sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                px: 3,
                py: 0.9,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {loading ? gettext("Sending...") : gettext("Send Feedback")}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
